import { useState, useEffect } from "react";
import { PrimaryButton, GhostButton, SecondaryButton } from "@/components/StandardButton";
import { Copy, Download, Mail, MessageSquare, Check, Lock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { DisplayM, BodyText, Caption } from "@/components/ui/typography";
import { useToast } from "@/hooks/use-toast";
import { generateFeedbackLink } from "@/utils/feedbackLinks";
import { supabase } from "@/integrations/supabase/client";

interface ShareStepProps {
  pageSlug: string;
  companyName: string;
  onNext: () => void;
  onSkip: () => void;
}

export const ShareStep = ({ pageSlug, companyName, onNext, onSkip }: ShareStepProps) => {
  const [copied, setCopied] = useState(false);
  const [passcodeCopied, setPasscodeCopied] = useState(false);
  const [passcode, setPasscode] = useState<string | null>(null);
  const { toast } = useToast();

  const feedbackUrl = generateFeedbackLink(companyName, pageSlug);

  // Fetch passcode for this page
  useEffect(() => {
    const fetchPasscode = async () => {
      const { data } = await supabase
        .from("feedback_pages")
        .select("passcode")
        .eq("slug", pageSlug)
        .single();
      
      if (data?.passcode) {
        setPasscode(data.passcode);
      }
    };
    fetchPasscode();
  }, [pageSlug]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(feedbackUrl);
      setCopied(true);
      toast({ title: "Link copied!" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ 
        title: "Failed to copy", 
        description: "Please copy the link manually",
        variant: "destructive" 
      });
    }
  };

  const handleCopyPasscode = async () => {
    if (!passcode) return;
    try {
      await navigator.clipboard.writeText(passcode);
      setPasscodeCopied(true);
      toast({ title: "Access code copied!" });
      setTimeout(() => setPasscodeCopied(false), 2000);
    } catch {
      toast({ 
        title: "Failed to copy", 
        description: "Please copy the code manually",
        variant: "destructive" 
      });
    }
  };

  const handleDownloadQR = () => {
    const svg = document.querySelector('#qr-code-svg svg') as SVGElement;
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 300;
      canvas.height = 300;
      ctx?.fillStyle && (ctx.fillStyle = '#ffffff');
      ctx?.fillRect(0, 0, canvas.width, canvas.height);
      ctx?.drawImage(img, 0, 0, 300, 300);
      
      const link = document.createElement('a');
      link.download = `${companyName}-feedback-qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast({ title: "QR Code downloaded!" });
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`Share your feedback with ${companyName}`);
    const passcodeText = passcode ? `\n\n🔐 Access Code: ${passcode}\n(You'll need this code to submit feedback)` : '';
    const body = encodeURIComponent(
      `Hi,\n\nWe'd love to hear your honest feedback about ${companyName}.\n\nAll responses are 100% anonymous.\n\nShare your thoughts here: ${feedbackUrl}${passcodeText}\n\nThank you!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleSlackShare = () => {
    const passcodeText = passcode ? ` | 🔐 Access Code: ${passcode}` : '';
    const text = encodeURIComponent(
      `📣 Share your feedback with ${companyName}!\n\nAll responses are 100% anonymous: ${feedbackUrl}${passcodeText}`
    );
    window.open(`https://slack.com/share?text=${text}`, '_blank');
    toast({
      title: "Share to Slack",
      description: "Copy the message to share in your Slack workspace",
    });
  };

  return (
    <div className="p-2 sm:p-4">
      {/* Header */}
      <div className="text-center mb-4">
        <DisplayM className="mb-1">Share your page</DisplayM>
        <BodyText className="text-muted-foreground text-sm">
          Your feedback page is ready. Here's how to share it.
        </BodyText>
      </div>

      {/* Copy Link Bar - Primary Action */}
      <div className="bg-muted/50 rounded-lg p-2 border mb-3">
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs sm:text-sm truncate bg-background rounded-lg px-2 py-1.5 border">
            {feedbackUrl}
          </code>
          <PrimaryButton 
            onClick={handleCopyLink} 
            size="sm"
            className="gap-1.5 shrink-0"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            <span className="hidden sm:inline">{copied ? "Copied!" : "Copy"}</span>
          </PrimaryButton>
        </div>
      </div>

      {/* Passcode Display */}
      {passcode && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-primary" />
              <div>
                <span className="text-sm font-medium">Access Code: </span>
                <span className="font-mono font-bold text-primary">{passcode}</span>
              </div>
            </div>
            <SecondaryButton 
              onClick={handleCopyPasscode} 
              size="sm"
              className="gap-1.5 shrink-0"
            >
              {passcodeCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </SecondaryButton>
          </div>
          <Caption className="text-muted-foreground mt-1.5">
            Recipients need this code to submit feedback
          </Caption>
        </div>
      )}

      {/* QR Code - Smaller on mobile */}
      <div className="flex flex-col items-center gap-2 mb-4">
        <div 
          className="bg-white p-3 rounded-lg border shadow-sm relative group cursor-pointer"
          onClick={handleDownloadQR}
          id="qr-code-svg"
        >
          <QRCodeSVG value={feedbackUrl} size={100} />
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
            <span className="text-white flex items-center gap-1.5 text-xs font-medium">
              <Download className="h-3.5 w-3.5" /> Download
            </span>
          </div>
        </div>
        <Caption className="text-muted-foreground text-xs">
          Click to download • Print for break rooms
        </Caption>
      </div>

      {/* Quick Share - Horizontal Pills */}
      <div className="flex justify-center gap-2 mb-4">
        <SecondaryButton onClick={handleEmailShare} size="sm" className="gap-1.5">
          <Mail className="h-4 w-4" /> Email
        </SecondaryButton>
        <SecondaryButton onClick={handleSlackShare} size="sm" className="gap-1.5">
          <MessageSquare className="h-4 w-4" /> Slack
        </SecondaryButton>
      </div>

      {/* Footer Actions */}
      <div className="flex gap-2">
        <GhostButton onClick={onSkip} className="flex-1 min-h-[44px] text-sm">
          I'll do this later
        </GhostButton>
        <PrimaryButton onClick={onNext} className="flex-1 min-h-[44px] text-sm">
          Next: Schedule Emails
        </PrimaryButton>
      </div>
    </div>
  );
};
